import Konva from 'konva';
import './csv.ts';
import { 
  initializeKonva, 
  getStage, 
  getLayer, 
  getDragLayer, 
  drawChart,
  calculateContentBounds,
  findDropTarget,
  createGuestNameGroup
} from './drawing.ts';
import { 
  $guests, 
  $dragState, 
  $occupiedSeats,
  $guestFilter,
  $guestFilterMode,
  $guestSortField,
  $guestSortDirection,
  $customFields,
  $currentVersionId,
  $availableVersions,
  $isAutoSaving,
  $canUndo,
  $canRedo,
  guestActions, 
  dragActions,
  versionActions
} from './state.ts';
import { historyAwareGuestActions, historyNavigation } from './history.ts';
import {
  getStageWidth,
  getStageHeight,
  SCALE_FACTOR,
  MIN_SCALE,
  MAX_SCALE,
  DROP_ZONE_COLOR,
  DROP_ZONE_SCALE,
  NAME_DRAG_BG_COLOR,
  NAME_DRAG_BORDER_COLOR,
  SIDEBAR_WIDTH
} from './constants.ts';
import { showDropZones, resetDropZones, setHoverSeat } from './ui/dropzones.ts';
import { showNotification } from './notifications.ts';
import { storageManager } from './storage.ts';
import type { Guest, GuestSortField } from './types.ts';
import { showGuestDialog } from './guest-dialog.ts';
import { 
  switchToVersion, 
  showRenameVersionDialog, 
  showForkVersionDialog, 
  showDeleteVersionDialog 
} from './version-dialogs.ts';

// Initialize sidebar state
async function initializeSidebarState(): Promise<void> {
  try {
    const isCollapsed = await storageManager.getSidebarCollapsed();
    const sidebar = document.querySelector('.sidebar') as HTMLElement;
    const mainContent = document.querySelector('.main-content') as HTMLElement;
    
    if (isCollapsed && sidebar && mainContent) {
      sidebar.classList.add('collapsed');
      mainContent.classList.add('sidebar-collapsed');
    }
  } catch (error) {
    console.error('Failed to load sidebar state:', error);
  }
}

// Initialize storage and load data
async function initializeStorage(): Promise<void> {
  try {
    // Try to load the last used version
    const lastVersion = await storageManager.getLastUsedVersion();
    
    // Load all available versions
    const allVersions = await storageManager.getAllVersions();
    versionActions.setAvailableVersions(allVersions);
    
    if (lastVersion) {
      // Set current version ID for auto-save
      versionActions.setCurrentVersion(lastVersion.id);
      
      // Load the version's state into the app
      $guests.set(lastVersion.state.guests);
      $customFields.set(lastVersion.state.customFields);
      
      // Load version history
      historyNavigation.loadVersionHistory(lastVersion.history || []);
      
      showNotification('Version Loaded', `Loaded "${lastVersion.name}"`, 'success');
    } else {
      // Create an initial version with the current default state if we have guests
      const currentGuests = $guests.get();
      const currentCustomFields = $customFields.get();
      
      if (currentGuests.length > 0) {
        const newVersion = await storageManager.createVersion(
          'Untitled Layout',
          { guests: currentGuests, customFields: currentCustomFields }
        );
        
        // Set current version ID for auto-save
        versionActions.setCurrentVersion(newVersion.id);
        versionActions.addVersion(newVersion);
        
        showNotification('Welcome', 'Created your first layout version', 'success');
      }
    }
  } catch (error) {
    console.error('Failed to initialize storage:', error);
    showNotification('Storage Error', 'Failed to load saved data. Using default state.', 'warning');
  }
}



// Module-level resize handler (needs to be accessible from multiple functions)
function handleResize(): void {
  const sidebar = document.querySelector('.sidebar');
  const sidebarCollapsed = sidebar ? sidebar.classList.contains('collapsed') : false;
  const newWidth = window.innerWidth - (sidebarCollapsed ? 0 : SIDEBAR_WIDTH);
  const newHeight = window.innerHeight;
  
  const stage = getStage();
  stage.width(newWidth);
  stage.height(newHeight);
  stage.batchDraw();
}

// Initialize the application
async function initializeApp(): Promise<void> {
  // Initialize sidebar state before calculating stage dimensions
  await initializeSidebarState();
  
  const stageWidth = getStageWidth();
  const stageHeight = getStageHeight();
  
  // Initialize Konva and wait for fonts to load
  await initializeKonva(stageWidth, stageHeight);
  
  // Initialize storage and load last used version
  await initializeStorage();
  
  const stage = getStage();
  const layer = getLayer();
  const dragLayer = getDragLayer();
  
  // Set up zoom functionality
  stage.on('wheel', (e) => {
    e.evt.preventDefault();
    
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    
    let newScale = direction > 0 ? oldScale * SCALE_FACTOR : oldScale / SCALE_FACTOR;

    newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

    stage.scale({ x: newScale, y: newScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    stage.position(newPos);
    stage.batchDraw();
  });

  // Create recenter functionality
  function recenterView() {
    const bounds = calculateContentBounds();
    const stageWidth = stage.width();
    const stageHeight = stage.height();
    
    // Calculate optimal scale to fit content with some padding
    const contentAspectRatio = bounds.width / bounds.height;
    const stageAspectRatio = stageWidth / stageHeight;
    
    let optimalScale: number;
    if (contentAspectRatio > stageAspectRatio) {
      // Content is wider relative to stage - fit by width
      optimalScale = (stageWidth * 0.8) / bounds.width;
    } else {
      // Content is taller relative to stage - fit by height  
      optimalScale = (stageHeight * 0.8) / bounds.height;
    }
    
    // Clamp to our scale limits
    optimalScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, optimalScale));
    
    // Calculate center position
    const contentCenterX = bounds.left + bounds.width / 2;
    const contentCenterY = bounds.top + bounds.height / 2;
    
    const viewportCenterX = stageWidth / 2;
    const viewportCenterY = stageHeight / 2;
    
    const targetX = viewportCenterX - contentCenterX * optimalScale;
    const targetY = viewportCenterY - contentCenterY * optimalScale;
    
    // Animate to the centered position
    stage.to({
      x: targetX,
      y: targetY,
      scaleX: optimalScale,
      scaleY: optimalScale,
      duration: 0.1,
      easing: Konva.Easings.EaseInOut
    });
  }

  // Create recenter button as HTML element
  const recenterButton = document.getElementById('recenterButton');
  if (recenterButton) {
    recenterButton.addEventListener('click', recenterView);
  }
  
  // Set up drag and drop functionality
  stage.on('dragstart', function (e) {
    if (!e.target.name()) {
      stage.container().style.cursor = 'move';
    }
    if (e.target.name() === 'guestName') {
      const nameGroup = e.target as Konva.Group;
      const guestId = nameGroup.attrs.guestId;

      const guest = guestActions.findGuest(guestId);
      const originalSeatKey =
        guest && guest.table && guest.seat
          ? `${guest.table}-${guest.seat}`
          : null;

      // Start drag in state
      dragActions.startDrag(guestId, {
        x: nameGroup.getAttr('originalX'),
        y: nameGroup.getAttr('originalY')
      }, originalSeatKey);
      
      // Style and Layer change for drag
      const nameBg = nameGroup.findOne('.nameBg');
      if (nameBg) {
        nameBg.to({
          fill: NAME_DRAG_BG_COLOR,
          stroke: NAME_DRAG_BORDER_COLOR,
          strokeWidth: 1,
          duration: 0.1,
        });
      }
      nameGroup.moveTo(dragLayer);

      nameGroup.moveToTop();
      stage.container().style.cursor = 'grabbing';

      // Show drop zones on empty seats
      showDropZones(stage, $occupiedSeats.get(), originalSeatKey);
      
      // No special unseat zone needed
      stage.draggable(false); // Disable stage panning while dragging a guest
      layer.draw();
      dragLayer.draw();
    }
  });

  stage.on('dragmove', function(e) {
    if (e.target.name() === 'guestName') {
      const targetSeat = findDropTarget();
      const occupiedSeats = $occupiedSeats.get();
      const dragState = $dragState.get();

      let emptyTargetSeat = null;
      let newHoveredSeatKey = null;
      if (targetSeat) {
        const seatKey = `${targetSeat.attrs.tableNumber}-${targetSeat.attrs.seatNumber}`;
        if (!occupiedSeats.has(seatKey) || seatKey === dragState.originalSeat) {
          emptyTargetSeat = targetSeat;
          newHoveredSeatKey = seatKey;
        }
      }

      if (dragState.hoveredSeat !== newHoveredSeatKey) {
        // Revert old hover seat by re-calling showDropZones is overkill; we keep it simple:
        // First restore base dropzone color for previously hovered if any:
        if (dragState.hoveredSeat) {
          const [oldTable, oldSeat] = dragState.hoveredSeat.split('-').map(Number);
          const oldSeatElement = stage.find('.seat').find(s =>
            s.attrs.tableNumber === oldTable && s.attrs.seatNumber === oldSeat
          );
          if (oldSeatElement) {
            oldSeatElement.to({ fill: DROP_ZONE_COLOR, scaleX: DROP_ZONE_SCALE, scaleY: DROP_ZONE_SCALE, duration: 0.1 });
          }
        }
        // Highlight the new one
        if (emptyTargetSeat) setHoverSeat(emptyTargetSeat as Konva.Shape);

        // Update state
        dragActions.updateHover(newHoveredSeatKey);
      }
    }
  });

  stage.on('dragend', function (e) {
    if (!e.target.name()) {
      stage.container().style.cursor = 'default';
      return;
    }
    if (e.target.name() === 'guestName') {
      const nameGroup = e.target as Konva.Group;
      const dragState = $dragState.get();
      const draggedGuest = guestActions.findGuest(dragState.draggedGuestId!);
      
      // Reset cursor and background
      stage.container().style.cursor = 'default';
      const nameBg = nameGroup.findOne('.nameBg');
      if (nameBg) { 
        nameBg.to({
          fill: 'transparent',
          stroke: 'transparent',
          strokeWidth: 0,
          duration: 0.2
        });
      }

      // Reset all drop zone visuals
      resetDropZones(stage, $occupiedSeats.get());
      
      if (dragState.hoveredSeat && draggedGuest) {
        // Dropped on a seat: update guest placement if it actually changed
        if (dragState.hoveredSeat !== dragState.originalSeat) {
          const [newTable, newSeat] = dragState.hoveredSeat.split('-').map(Number);
          historyAwareGuestActions.moveGuest(dragState.draggedGuestId!, newTable, newSeat);

          showNotification(
            'Guest Moved',
            `${draggedGuest.fullName} has been moved to table ${newTable} seat ${newSeat}`,
            'success'
          );
        }
      } else if (dragState.originalSeat && draggedGuest) {
        // Dropped elsewhere from a seated position: unplace the guest
        historyAwareGuestActions.unplaceGuest(dragState.draggedGuestId!);

        showNotification(
          'Guest Unseated',
          `${draggedGuest.fullName} has been moved to the unplaced guests list`,
          'success'
        );
      }
        
        // The nameGroup is on dragLayer and is now stale. Destroy it.
        nameGroup.destroy();
        dragLayer.draw();
        

      }
      
      // End drag state
      dragActions.endDrag();
      stage.draggable(true); // Re-enable stage panning
  });

  window.addEventListener('resize', handleResize);
  handleResize();

  // Set initial zoom and center the content
  const bounds = calculateContentBounds();
  const contentAspectRatio = bounds.width / bounds.height;
  const viewportAspectRatio = stageWidth / stageHeight;
  let initialScale = contentAspectRatio > viewportAspectRatio
    ? (stageWidth * 0.9) / bounds.width
    : (stageHeight * 0.9) / bounds.height;
  initialScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, initialScale));

  stage.scale({ x: initialScale, y: initialScale });

  const contentCenterX = bounds.left + bounds.width / 2;
  const contentCenterY = bounds.top + bounds.height / 2;
  
  const viewportCenterX = stageWidth / 2;
  const viewportCenterY = stageHeight / 2;
  
  const stageX = viewportCenterX - contentCenterX * initialScale;
  const stageY = viewportCenterY - contentCenterY * initialScale;
  
  stage.position({ x: stageX, y: stageY });

  // Draw initial chart
  drawChart();

  // Initialize version management
  initializeVersionManagement();
  
  // Initialize guest list
  initializeGuestList();
  
  // Initialize custom fields section
  initializeCustomFields();

  // Set up Add New Guest button
  const addNewGuestBtn = document.getElementById('addNewGuestBtn');
  if (addNewGuestBtn) {
    addNewGuestBtn.addEventListener('click', () => showGuestDialog());
  }

  // Set up state change listeners for redrawing

  // Listen for guest changes to redraw chart
  $guests.listen(() => {
    layer.destroyChildren();
    drawChart();
    layer.draw();
  });

  // Listen for custom field visibility changes to redraw chart
  $customFields.listen(() => {
    layer.destroyChildren();
    drawChart();
    layer.draw();
  });
}

// Version management functionality
function initializeVersionManagement(): void {
  const versionDropdown = document.getElementById('versionDropdown') as HTMLSelectElement;
  const editVersionBtn = document.getElementById('editVersionBtn') as HTMLButtonElement;
  const forkVersionBtn = document.getElementById('forkVersionBtn') as HTMLButtonElement;
  const deleteVersionBtn = document.getElementById('deleteVersionBtn') as HTMLButtonElement;
  const undoBtn = document.getElementById('undoBtn') as HTMLButtonElement;
  const redoBtn = document.getElementById('redoBtn') as HTMLButtonElement;
  const saveCheckmark = document.getElementById('saveCheckmark') as HTMLElement;
  const saveText = document.getElementById('saveText') as HTMLSpanElement;

  if (!versionDropdown || !editVersionBtn || !forkVersionBtn || !deleteVersionBtn) {
    console.warn('Version management elements not found');
    return;
  }

  // Function to update version dropdown
  function updateVersionDropdown(): void {
    const versions = $availableVersions.get();
    const currentVersionId = $currentVersionId.get();
    
    // Clear existing options except the first placeholder
    versionDropdown.innerHTML = '<option value="">Select version...</option>';
    
    versions.forEach(version => {
      const option = document.createElement('option');
      option.value = version.id;
      option.textContent = version.name;
      if (version.id === currentVersionId) {
        option.selected = true;
      }
      versionDropdown.appendChild(option);
    });
    
    // Enable/disable action buttons based on selection
    const hasSelection = currentVersionId !== null;
    editVersionBtn.disabled = !hasSelection;
    forkVersionBtn.disabled = !hasSelection;
    deleteVersionBtn.disabled = !hasSelection || versions.length <= 1;
  }

  // Function to update save indicator
  function updateSaveIndicator(): void {
    const isAutoSaving = $isAutoSaving.get();
    
    if (isAutoSaving) {
      saveCheckmark.style.display = 'none';
      saveText.textContent = 'Saving...';
    } else {
      setTimeout(() => {
        saveCheckmark.style.display = 'block';
        saveText.textContent = 'Saved';
      }, 1000);

      setTimeout(() => {
        saveCheckmark.style.display = 'none';
      }, 2000);
    }
  }

  // Function to update undo/redo button states
  function updateHistoryButtons(): void {
    const canUndo = $canUndo.get();
    const canRedo = $canRedo.get();
    
    undoBtn.disabled = !canUndo;
    redoBtn.disabled = !canRedo;
  }

  // Subscribe to state changes
  $availableVersions.subscribe(updateVersionDropdown);
  $currentVersionId.subscribe(updateVersionDropdown);
  $isAutoSaving.subscribe(updateSaveIndicator);
  $canUndo.subscribe(updateHistoryButtons);
  $canRedo.subscribe(updateHistoryButtons);

  // Version dropdown change handler
  versionDropdown.addEventListener('change', async (e) => {
    const target = e.target as HTMLSelectElement;
    const selectedVersionId = target.value;
    
    if (!selectedVersionId) return;
    
    try {
      await switchToVersion(selectedVersionId);
    } catch (error) {
      console.error('Failed to switch version:', error);
      showNotification('Error', 'Failed to switch version', 'error');
    }
  });

  // Edit version name
  editVersionBtn.addEventListener('click', () => {
    const currentVersionId = $currentVersionId.get();
    if (currentVersionId) {
      showRenameVersionDialog(currentVersionId);
    }
  });

  // Fork version
  forkVersionBtn.addEventListener('click', () => {
    const currentVersionId = $currentVersionId.get();
    if (currentVersionId) {
      showForkVersionDialog(currentVersionId);
    }
  });

  // Delete version
  deleteVersionBtn.addEventListener('click', () => {
    const currentVersionId = $currentVersionId.get();
    if (currentVersionId) {
      showDeleteVersionDialog(currentVersionId);
    }
  });

  // Undo/Redo buttons
  undoBtn.addEventListener('click', () => {
    historyNavigation.undo();
  });

  redoBtn.addEventListener('click', () => {
    historyNavigation.redo();
  });

  // Sidebar toggle functionality
  const sidebarToggle = document.getElementById('sidebarToggle') as HTMLButtonElement;
  const sidebar = document.querySelector('.sidebar') as HTMLElement;
  const mainContent = document.querySelector('.main-content') as HTMLElement;
  
  if (sidebarToggle && sidebar && mainContent) {
    sidebarToggle.addEventListener('click', async () => {
      sidebar.classList.toggle('collapsed');
      mainContent.classList.toggle('sidebar-collapsed');
      
      // Save sidebar state
      const isCollapsed = sidebar.classList.contains('collapsed');
      try {
        await storageManager.setSidebarCollapsed(isCollapsed);
      } catch (error) {
        console.error('Failed to save sidebar state:', error);
      }
      
      // Trigger resize after animation completes (300ms)
      setTimeout(() => {
        handleResize();
      }, 300);
    });
  }

  // Keyboard shortcuts for undo/redo
  document.addEventListener('keydown', (e) => {
    // Undo: Ctrl+Z (Cmd+Z on Mac)
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      historyNavigation.undo();
    }
    
    // Redo: Ctrl+Shift+Z or Ctrl+Y (Cmd+Shift+Z or Cmd+Y on Mac)
    if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
      e.preventDefault();
      historyNavigation.redo();
    }
  });
}



// Guest list functionality
function initializeGuestList(): void {
  const guestListElement = document.getElementById('guestList') as HTMLDivElement;
  const guestListContainer = document.querySelector('.guest-list-container') as HTMLDivElement;
  const guestList = document.querySelector('.guest-list') as HTMLDivElement;
  const searchInput = document.getElementById('guestSearch') as HTMLInputElement;
  if (!guestListElement || !guestListContainer || !guestList || !searchInput) {
    console.warn('Guest list elements not found');
    return;
  }

  // Set up search input
  searchInput.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement;
    $guestFilter.set(target.value.toLowerCase());
  });

  // Set up filter toggle buttons
  const filterButtons = document.querySelectorAll('.filter-toggle');
  filterButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      const target = e.target as HTMLButtonElement;
      const filterMode = target.dataset.filter as 'unseated' | 'seated' | 'all';
      
      // Update active button
      filterButtons.forEach(btn => btn.classList.remove('active'));
      target.classList.add('active');
      
      // Update filter mode state
      $guestFilterMode.set(filterMode);
    });
  });

  // Set up scroll listener to handle top and bottom shadows
  guestList.addEventListener('scroll', () => {
    const isAtBottom = guestList.scrollTop + guestList.clientHeight >= guestList.scrollHeight - 1;
    const isAtTop = guestList.scrollTop <= 1; // Allow for small rounding errors
    const hasOverflow = guestList.scrollHeight > guestList.clientHeight;
    
    // Handle bottom shadow
    if (isAtBottom) {
      guestListContainer.classList.remove('has-overflow');
    } else {
      // Only add the class back if there's actually overflow
      if (hasOverflow) {
        guestListContainer.classList.add('has-overflow');
      }
    }
    
    // Handle header shadow (shows when scrolled away from top)
    const guestListHeader = document.querySelector('.guest-list-header') as HTMLDivElement;
    if (guestListHeader) {
      if (isAtTop || !hasOverflow) {
        guestListHeader.classList.remove('has-shadow');
      } else {
        guestListHeader.classList.add('has-shadow');
      }
    }
  });

  function createSortIndicator(direction: 'asc' | 'desc'): string {
    const svgUp = "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='lucide lucide-arrow-up'><path d='M12 19V5'/><path d='M5 12l7-7 7 7'/></svg>";
    const svgDown = "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='lucide lucide-arrow-down'><path d='M12 5v14'/><path d='M19 12l-7 7-7-7'/></svg>";
    return direction === 'asc' ? svgUp : svgDown;
  }

  function updateGuestHeader(): void {
    guestListElement.innerHTML = '';
    const header = document.createElement('div');
    header.className = 'guest-list-header';
    
    const currentSortField = $guestSortField.get();
    const currentSortDirection = $guestSortDirection.get();
    
    // Create single line name/table column header
    const nameTableCell = document.createElement('div');
    nameTableCell.className = 'single-line-header';
    
    // Name section (clickable)
    const nameSection = document.createElement('span');
    nameSection.className = 'sortable header-part';
    nameSection.dataset.field = 'fullName';
    if (currentSortField === 'fullName') {
      nameSection.classList.add('sort-active');
      nameSection.innerHTML = 'Name<span class="sort-indicator">' + createSortIndicator(currentSortDirection) + '</span>';
    } else {
      nameSection.textContent = 'Name';
    }
    
    // Separator
    const separator = document.createElement('span');
    separator.className = 'header-separator';
    separator.textContent = ' | ';
    
    // Table section (clickable)
    const tableSection = document.createElement('span');
    tableSection.className = 'sortable header-part';
    tableSection.dataset.field = 'table';
    if (currentSortField === 'table') {
      tableSection.classList.add('sort-active');
      tableSection.innerHTML = 'Table<span class="sort-indicator">' + createSortIndicator(currentSortDirection) + '</span>';
    } else {
      tableSection.textContent = 'Table';
    }
    
    // Add all parts to the cell
    nameTableCell.appendChild(nameSection);
    nameTableCell.appendChild(separator);
    nameTableCell.appendChild(tableSection);
    header.appendChild(nameTableCell);
    
    // Create custom field headers
    const visibleFields = $customFields.get().filter(c => c.visibleInList);
    visibleFields.forEach(cfg => {
      const div = document.createElement('div');
      div.className = 'sortable';
      div.dataset.field = cfg.key;
      if (currentSortField === cfg.key) {
        div.classList.add('sort-active');
      }
      
      const content = document.createElement('span');
      content.textContent = cfg.displayName;
      div.appendChild(content);
      
      const indicator = document.createElement('div');
      indicator.className = 'sort-indicator';
      indicator.innerHTML = createSortIndicator(currentSortDirection);
      div.appendChild(indicator);
      
      header.appendChild(div);
    });
    
    guestListContainer.style.setProperty('--custom-field-count', String(visibleFields.length));
    guestListElement.appendChild(header);
    
    // Add click handlers to sortable headers
    header.querySelectorAll('.sortable').forEach(headerCell => {
      headerCell.addEventListener('click', () => {
        const htmlElement = headerCell as HTMLElement;
        const field = htmlElement.dataset.field as GuestSortField;
        if (field) {
          handleHeaderClick(field);
        }
      });
    });
  }

  function handleHeaderClick(field: GuestSortField): void {
    const currentField = $guestSortField.get();
    const currentDirection = $guestSortDirection.get();
    
    if (currentField === field) {
      // Toggle direction if clicking on the same field
      $guestSortDirection.set(currentDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and reset to ascending
      $guestSortField.set(field);
      $guestSortDirection.set('asc');
    }
      
    // Scroll guest list to top when initializing a new sort
    guestList.scrollTop = 0;
  }



  // Function to render guest list
  function renderGuestList(): void {
    const allGuests = $guests.get();
    const filter = $guestFilter.get();
    const filterMode = $guestFilterMode.get();
    const sortField = $guestSortField.get();
    const sortDirection = $guestSortDirection.get();
    
    // Filter guests based on mode
    let guestsToShow = allGuests;
    if (filterMode === 'unseated') {
      guestsToShow = allGuests.filter(guest => !guest.table || !guest.seat);
    } else if (filterMode === 'seated') {
      guestsToShow = allGuests.filter(guest => guest.table && guest.seat);
    }
    // 'all' mode shows all guests (no additional filtering)
    
    // Filter guests based on search input
    const filteredGuests = guestsToShow.filter(guest =>
      guest.fullName.toLowerCase().includes(filter)
    );

    let sortedGuests: Guest[];
    
    // For custom fields, handle null values specially to always keep them at bottom
    if (sortField !== 'fullName' && sortField !== 'table') {
      // Separate guests with and without the custom field
      const guestsWithValue: Guest[] = [];
      const guestsWithoutValue: Guest[] = [];
      
      filteredGuests.forEach(guest => {
        const customValue = (guest as any)[sortField];
        if (customValue) {
          guestsWithValue.push(guest);
        } else {
          guestsWithoutValue.push(guest);
        }
      });
      
      // Sort only the guests with values
      const sortedWithValue = guestsWithValue.sort((a, b) => {
        const valA = ((a as any)[sortField] as string).toLowerCase();
        const valB = ((b as any)[sortField] as string).toLowerCase();
        
        if (valA < valB) return -1;
        if (valA > valB) return 1;
        return 0;
      });
      
      if (sortDirection === 'desc') {
        sortedWithValue.reverse();
      }
      
      // Combine: sorted guests with values first, then guests without values
      sortedGuests = [...sortedWithValue, ...guestsWithoutValue];
    } else {
      // For name and table sorting, use the original logic
      sortedGuests = [...filteredGuests].sort((a, b) => {
        const getVal = (g: Guest): any => {
          switch (sortField) {
            case 'fullName':
              return g.fullName.toLowerCase();
            case 'table':
              // For table sorting, create a compound key: table number then seat number
              const table = g.table ?? Infinity;
              const seat = g.seat ?? Infinity;
              return [table, seat];
            default:
              return '';
          }
        };
        
        const valA = getVal(a);
        const valB = getVal(b);
        
        // Handle table sorting with compound comparison
        if (sortField === 'table') {
          const [tableA, seatA] = valA;
          const [tableB, seatB] = valB;
          
          // First compare by table
          if (tableA < tableB) return -1;
          if (tableA > tableB) return 1;
          
          // If tables are equal, compare by seat
          if (seatA < seatB) return -1;
          if (seatA > seatB) return 1;
          return 0;
        } else {
          // Standard comparison for other fields
          if (valA < valB) return -1;
          if (valA > valB) return 1;
          return 0;
        }
      });
      
      if (sortDirection === 'desc') {
        sortedGuests.reverse();
      }
    }

    // Header
    updateGuestHeader();

    if (sortedGuests.length === 0) {
      const emptyTemplate = document.getElementById('guest-list-empty-template') as HTMLTemplateElement;
      const emptyMessage = emptyTemplate.content.cloneNode(true) as DocumentFragment;
      const emptyDiv = emptyMessage.querySelector('.guest-item') as HTMLDivElement;
      
      if (filter) {
        emptyDiv.textContent = 'No guests match your search';
      } else {
        switch (filterMode) {
          case 'unseated':
            emptyDiv.textContent = 'No unseated guests';
            break;
          case 'seated':
            emptyDiv.textContent = 'No seated guests';
            break;
          case 'all':
            emptyDiv.textContent = 'No guests';
            break;
        }
      }
      
      guestListElement.appendChild(emptyMessage);
      return;
    }

    // Create guest items
    sortedGuests.forEach(guest => {
      const guestTemplate = document.getElementById('guest-item-template') as HTMLTemplateElement;
      const guestFragment = guestTemplate.content.cloneNode(true) as DocumentFragment;

      const guestItem = guestFragment.querySelector('.guest-item') as HTMLDivElement;
      const guestName = guestFragment.querySelector('.guest-name') as HTMLDivElement;
      const guestSeatInfo = guestFragment.querySelector('.guest-seat-info') as HTMLDivElement;
      const fieldCells = guestFragment.querySelectorAll('.custom-field-cell') as NodeListOf<HTMLDivElement>;
      const seatButton = guestFragment.querySelector('.seat-button') as HTMLButtonElement;
      
      // Set guest data
      guestItem.dataset.guestId = guest.id.toString();
      guestName.textContent = guest.fullName;
      guestName.title = guest.fullName; // Tooltip for long names
      
      // Show seat info for seated guests, or [Unseated] for unseated guests
      if (guest.table && guest.seat) {
        guestSeatInfo.textContent = `Table ${guest.table}, Seat ${guest.seat}`;
        guestSeatInfo.style.display = 'block';
        seatButton.title = `Edit ${guest.fullName}`;
        seatButton.setAttribute('aria-label', `Edit guest information for ${guest.fullName}`);
      } else {
        guestSeatInfo.textContent = 'No seat';
        guestSeatInfo.style.display = 'block';
        seatButton.title = `Edit ${guest.fullName}`;
        seatButton.setAttribute('aria-label', `Edit guest information for ${guest.fullName}`);
      }
      
      const configs = $customFields.get();
      fieldCells.forEach(cell => {
        const key = cell.dataset.field as string;
        const cfg = configs.find(c => c.key === key);
        const value = (guest as any)[key];
        if (cfg && cfg.visibleInList) {
          cell.style.display = 'block';
          cell.textContent = value ? String(value) : '';
          cell.title = value ? `${cfg.displayName}: ${value}` : cfg.displayName;
        } else {
          cell.style.display = 'none';
        }
      });

      // Add click handler
      seatButton.addEventListener('click', () => {
        showGuestDialog(guest);
      });

      guestItem.addEventListener('dragstart', (ev) => ev.preventDefault());
      guestItem.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        const target = ev.target as HTMLElement;
        if (target.closest('.seat-button')) {
          return;
        }
        startGuestDragFromList(guest, ev as MouseEvent);
      });

      guestListElement.appendChild(guestFragment);
    });

    // Check if content overflows and add/remove shadow indicators
    // Use requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      const hasOverflow = guestList.scrollHeight > guestList.clientHeight;
      const isAtTop = guestList.scrollTop <= 1;
      
      // Handle bottom shadow
      guestListContainer.classList.toggle('has-overflow', hasOverflow);
      
      // Handle header shadow (only show if scrolled and has overflow)
      const guestListHeader = document.querySelector('.guest-list-header') as HTMLDivElement;
      if (guestListHeader) {
        guestListHeader.classList.toggle('has-shadow', hasOverflow && !isAtTop);
      }
    });
  }

  // Listen for state changes
  $guests.listen(renderGuestList);
  $guestFilter.listen(renderGuestList);
  $guestFilterMode.listen(renderGuestList);
  $guestSortField.listen(() => {
    updateGuestHeader();
    renderGuestList();
  });
  $guestSortDirection.listen(() => {
    updateGuestHeader();
    renderGuestList();
  });
  $customFields.listen(() => {
    updateGuestHeader();
    renderGuestList();
  });

  // Initial render
  renderGuestList();
}

// Custom fields functionality
function initializeCustomFields(): void {
  const customFieldsSection = document.getElementById('customFieldsSection') as HTMLDivElement;
  const customFieldToggles = document.getElementById('customFieldToggles') as HTMLDivElement;

  if (!customFieldsSection || !customFieldToggles) {
    console.warn('Custom fields elements not found');
    return;
  }

  function renderCustomFieldToggles(): void {
    const customFieldConfigs = $customFields.get();
    
    // Show/hide the section based on whether we have custom fields
    if (customFieldConfigs.length === 0) {
      customFieldsSection.style.display = 'none';
      return;
    }
    
    customFieldsSection.style.display = 'block';
    customFieldToggles.innerHTML = '';

    // Clone the table template
    const tableTemplate = document.getElementById('custom-field-visibility-table-template') as HTMLTemplateElement;
    const tableFragment = tableTemplate.content.cloneNode(true) as DocumentFragment;
    const table = tableFragment.querySelector('.custom-field-visibility-table') as HTMLTableElement;
    const tbody = table.querySelector('tbody') as HTMLTableSectionElement;
    
    // Create row for each custom field
    customFieldConfigs.forEach(config => {
      const rowTemplate = document.getElementById('custom-field-visibility-row-template') as HTMLTemplateElement;
      const rowFragment = rowTemplate.content.cloneNode(true) as DocumentFragment;
      
      const nameCell = rowFragment.querySelector('.field-name-cell') as HTMLTableCellElement;
      const chartCheckbox = rowFragment.querySelector('.chart-checkbox') as HTMLInputElement;
      const listCheckbox = rowFragment.querySelector('.list-checkbox') as HTMLInputElement;
      
      // Set field name
      nameCell.textContent = config.displayName;
      
      // Set checkbox states and attributes
      chartCheckbox.checked = config.visibleInChart;
      chartCheckbox.dataset.fieldKey = config.key;
      chartCheckbox.dataset.visibilityType = 'chart';
      
      listCheckbox.checked = config.visibleInList;
      listCheckbox.dataset.fieldKey = config.key;
      listCheckbox.dataset.visibilityType = 'list';
      
      // Add event listeners
      chartCheckbox.addEventListener('change', () => {
        const updatedConfigs = $customFields.get().map(c =>
          c.key === config.key ? { ...c, visibleInChart: chartCheckbox.checked } : c
        );
        $customFields.set(updatedConfigs);
      });
      
      listCheckbox.addEventListener('change', () => {
        const updatedConfigs = $customFields.get().map(c =>
          c.key === config.key ? { ...c, visibleInList: listCheckbox.checked } : c
        );
        $customFields.set(updatedConfigs);
      });
      
      tbody.appendChild(rowFragment);
    });
    
    customFieldToggles.appendChild(tableFragment);
  }

  // Listen for custom field changes
  $customFields.listen(renderCustomFieldToggles);

  // Initial render
  renderCustomFieldToggles();
}

function startGuestDragFromList(guest: Guest, ev: MouseEvent): void {
  const stage = getStage();
  const dragLayer = getDragLayer();
  const layer = getLayer();

  stage.setPointersPositions(ev);
  window.getSelection()?.removeAllRanges();

  const guestItem = ev.currentTarget as HTMLElement;
  const sidebar = document.querySelector('.sidebar') as HTMLElement | null;
  const chartContainer = document.querySelector('.chart-container') as HTMLElement;

  const existing = stage.findOne((node: Konva.Node) =>
    node.name() === 'guestName' && node.getAttr('guestId') === guest.id
  ) as Konva.Group | undefined;

  if (existing) {
    existing.visible(false);
    layer.draw();
  }

  const dragGroup = createGuestNameGroup(guest);
  dragGroup.visible(false);
  dragLayer.add(dragGroup);
  dragLayer.draw();

  const rect = guestItem.getBoundingClientRect();
  const offsetX = ev.clientX - rect.left;
  const offsetY = ev.clientY - rect.top;

  const ghost = guestItem.cloneNode(true) as HTMLElement;
  ghost.classList.add('guest-drag-ghost');
  ghost.style.width = `${rect.width}px`;
  ghost.style.height = `${rect.height}px`;
  ghost.style.left = `${rect.left}px`;
  ghost.style.top = `${rect.top}px`;
  document.body.appendChild(ghost);

  guestItem.classList.add('dragging');
  sidebar?.classList.add('sidebar--dimmed');

  let enteredCanvas = false;

  const onMove = (moveEv: MouseEvent) => {
    ghost.style.left = `${moveEv.clientX - offsetX}px`;
    ghost.style.top = `${moveEv.clientY - offsetY}px`;
    stage.setPointersPositions(moveEv);

    if (!enteredCanvas) {
      const canvasRect = chartContainer.getBoundingClientRect();
      if (
        moveEv.clientX >= canvasRect.left &&
        moveEv.clientX <= canvasRect.right &&
        moveEv.clientY >= canvasRect.top &&
        moveEv.clientY <= canvasRect.bottom
      ) {
        enteredCanvas = true;
        ghost.remove();
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const scale = stage.scaleX();
          const stagePos = stage.position();
          const stagePoint = {
            x: (pointer.x - stagePos.x) / scale,
            y: (pointer.y - stagePos.y) / scale,
          };
          dragGroup.position(stagePoint);
          dragGroup.setAttr('originalX', stagePoint.x);
          dragGroup.setAttr('originalY', stagePoint.y);
        }
        dragGroup.visible(true);
        dragLayer.draw();
        dragGroup.startDrag();
      }
    }
  };

  const onUp = (upEv: MouseEvent) => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
    sidebar?.classList.remove('sidebar--dimmed');
    guestItem.classList.remove('dragging');
    ghost.remove();

    if (enteredCanvas) {
      stage.setPointersPositions(upEv);
      dragGroup.stopDrag();
    } else {
      dragGroup.destroy();
      dragLayer.draw();
    }
  };

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
  onMove(ev);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeApp().catch(error => {
      console.error('Failed to initialize app:', error);
    });
  });
} else {
  initializeApp().catch(error => {
    console.error('Failed to initialize app:', error);
  });
} 
